/**
 * Web Token Manager
 *
 * Handles token storage, retrieval, validation, and refresh logic for web.
 * Uses localStorage directly (no Electron IPC needed).
 */

import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('web:auth:token-manager');

const TOKEN_STORAGE_KEY = 'ctrl-enter-tokens';

export type TokenData = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
};

export type TokenResult = {
  success: boolean;
  tokens?: TokenData | null;
  error?: string;
};

export type RefreshResult = {
  success: boolean;
  tokens?: TokenData;
  error?: string;
};

/**
 * Get API base URL from environment
 */
const getApiBaseUrl = (): string => {
  // In development, use Vite proxy or localhost
  // In production, use environment variable or default
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
};

/**
 * Get tokens from localStorage
 */
export const getStoredTokens = (): TokenResult => {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) {
      return { success: true, tokens: null };
    }

    const tokens: TokenData = JSON.parse(stored);
    return { success: true, tokens };
  } catch (error) {
    logger.error('❌ [TOKEN-MANAGER] Error getting stored tokens:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error getting tokens',
    };
  }
};

/**
 * Store tokens in localStorage
 */
export const storeTokens = (
  accessToken: string,
  refreshToken: string,
  expiresAt: number
): { success: boolean; error?: string } => {
  try {
    const tokens: TokenData = { accessToken, refreshToken, expiresAt };
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    logger.info('✅ [TOKEN-MANAGER] Tokens stored successfully');

    // Dispatch event to notify auth provider
    window.dispatchEvent(new CustomEvent('auth-tokens-changed'));

    return { success: true };
  } catch (error) {
    logger.error('❌ [TOKEN-MANAGER] Error storing tokens:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error storing tokens',
    };
  }
};

/**
 * Clear tokens from localStorage
 */
export const clearStoredTokens = (): { success: boolean; error?: string } => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    logger.info('✅ [TOKEN-MANAGER] Tokens cleared successfully');
    return { success: true };
  } catch (error) {
    logger.error('❌ [TOKEN-MANAGER] Error clearing tokens:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error clearing tokens',
    };
  }
};

/**
 * Check if a token is expired (with optional buffer time)
 */
export const isTokenExpired = (expiresAt: number, bufferMinutes = 0.5): boolean => {
  const now = Math.floor(Date.now() / 1000);
  const bufferTime = bufferMinutes * 60;
  return expiresAt <= now + bufferTime;
};

/**
 * Refresh tokens using the refresh token with retry logic
 */
export const refreshTokens = async (
  refreshToken: string,
  retryCount = 0
): Promise<RefreshResult> => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = Math.min(1000 * Math.pow(2, retryCount), 10000); // Exponential backoff, max 10s
  const API_BASE_URL = getApiBaseUrl();

  try {
    const { getDeviceId } = await import('../api/client');
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-app-source': 'enter-ctrl-web',
        'x-device-id': getDeviceId(), // Include device ID for consistency
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      let errorDetails = 'Unknown error';
      try {
        const errorBody = await response.text();
        errorDetails = errorBody;
      } catch {
        // Ignore error reading response
      }

      // Check if this is a transient error that should be retried
      const isRetryable =
        response.status >= 500 || // Server errors (5xx)
        response.status === 429 || // Rate limiting
        response.status === 408 || // Request timeout
        response.status === 504 || // Gateway timeout
        response.status === 503; // Service unavailable

      if (isRetryable && retryCount < MAX_RETRIES) {
        logger.warn(
          `⚠️ [TOKEN-MANAGER] Token refresh failed (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying in ${RETRY_DELAY}ms...`,
          { status: response.status, error: errorDetails }
        );
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return refreshTokens(refreshToken, retryCount + 1);
      }

      logger.error('❌ [TOKEN-MANAGER] Token refresh failed:', response.status, errorDetails);
      return {
        success: false,
        error: `Token refresh failed with status ${response.status}: ${errorDetails}`,
      };
    }

    const refreshData = await response.json();

    if (!refreshData.accessToken || !refreshData.refreshToken) {
      logger.error('❌ [TOKEN-MANAGER] Invalid refresh response - missing tokens');
      return {
        success: false,
        error: 'Invalid refresh response - missing tokens',
      };
    }

    const newTokens: TokenData = {
      accessToken: refreshData.accessToken,
      refreshToken: refreshData.refreshToken,
      expiresAt: refreshData.expiresAt,
    };

    // Store the new tokens
    const storeResult = storeTokens(
      newTokens.accessToken,
      newTokens.refreshToken,
      newTokens.expiresAt
    );

    if (!storeResult.success) {
      logger.error('❌ [TOKEN-MANAGER] Failed to store refreshed tokens:', storeResult.error);
      return {
        success: false,
        error: `Failed to store refreshed tokens: ${storeResult.error}`,
      };
    }

    logger.info('✅ [TOKEN-MANAGER] Tokens refreshed successfully');
    return {
      success: true,
      tokens: newTokens,
    };
  } catch (error) {
    logger.error('❌ [TOKEN-MANAGER] Unexpected error during token refresh:', error);

    // Retry on network errors
    if (retryCount < MAX_RETRIES) {
      logger.warn(
        `⚠️ [TOKEN-MANAGER] Retrying token refresh (attempt ${retryCount + 1}/${MAX_RETRIES}) in ${RETRY_DELAY}ms...`
      );
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return refreshTokens(refreshToken, retryCount + 1);
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during token refresh',
    };
  }
};

/**
 * Get valid access token (refresh if needed)
 * Returns null if no tokens or refresh fails
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  const tokenResult = getStoredTokens();

  if (!tokenResult.success || !tokenResult.tokens) {
    return null;
  }

  const { tokens } = tokenResult;

  // If token is still valid, return it
  if (!isTokenExpired(tokens.expiresAt)) {
    return tokens.accessToken;
  }

  // Token expired, try to refresh
  logger.info('🔄 [TOKEN-MANAGER] Access token expired, refreshing...');
  const refreshResult = await refreshTokens(tokens.refreshToken);

  if (refreshResult.success && refreshResult.tokens) {
    logger.info('✅ [TOKEN-MANAGER] Token refreshed successfully');
    return refreshResult.tokens.accessToken;
  }

  // Refresh failed, clear invalid tokens
  logger.warn('⚠️ [TOKEN-MANAGER] Token refresh failed, clearing tokens');
  clearStoredTokens();
  return null;
};

/**
 * Validate tokens with server
 * Useful for checking if stored tokens are still valid on app startup
 */
export const validateTokensWithServer = async (): Promise<boolean> => {
  const accessToken = await getValidAccessToken();
  if (!accessToken) {
    return false;
  }

  const API_BASE_URL = getApiBaseUrl();

  try {
    const { getDeviceId } = await import('../api/client');
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-app-source': 'enter-ctrl-web',
        'x-device-id': getDeviceId(), // CRITICAL: Include device ID
      },
    });

    return response.ok;
  } catch (error) {
    logger.error('❌ [TOKEN-MANAGER] Error validating tokens:', error);
    return false;
  }
};
